import { css } from '@emotion/react';
import { IonContent, IonPage } from '@ionic/react';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useMap } from 'react-map-gl/maplibre';
import { type RouteComponentProps, Redirect } from 'react-router';
import * as commonCss from '../../styles/common';

import { TourProvider } from '@reactour/tour';
import { useGetAirQualityMarkers } from '../../api/queries/getAirQualityMarkers';
import { useGetOpenSites } from '../../api/queries/getOpenSites';
import { useGetSingleNetwork } from '../../api/queries/getSingleNetworks';
import { AnalyticsEventName } from '../../common/analytics/types';
import { trackEvent } from '../../common/analytics/utils';
import { DefaultSettings } from '../../common/default-settings';
import { LocationFetchError, LocationOOBException, WLOMException } from '../../common/exceptions';
import { LocationSearchResult, SearchResultType } from '../../common/location-search-result';
import { findNearestDatasourceId, findWorstPollutedDatasourceId, getZoomForRadius } from '../../common/location-utils';
import { MapData } from '../../common/map-data';
import { updateMapUrl } from '../../common/url-utils';
import { isWidgetMode } from '../../common/widgets-utils';
import BuildInfo from '../../components/BuildInfo';
import DatasourceDetailsContainer from '../../components/DatasourceDetailsContainer';
import ExploreFurther from '../../components/ExploreFurther';
import MapAttribution from '../../components/MapAttribution';
import MapCard from '../../components/MapCard';
import MaplibreMap from '../../components/MaplibreMap';
import MapMarkerLegend from '../../components/MapMarkerLegend';
import { MapMenuButton } from '../../components/MapMenuButton';
import { dismissTour, UITour, UITourSteps } from '../../components/UITour';
import { useSettings } from '../../contexts/SettingsContext';
import useWindowSize from '../../hooks/window-size';
import BottomSheet from '../../layouts/BottomSheet';
import { SidePanel } from '../../layouts/SidePanel';
import { ToastMsg } from '../../layouts/ToastMsg';
import { DatasourceId, NetworkId, Point, SourceType } from '../../types';
import { MapPageUrlSettings } from './url-settings';
import { ActiveLocationType } from '../../common/active-location';
import useElementHeight from '../../hooks/element-height';

const MapPage: React.FC<RouteComponentProps<{ networkId: NetworkId; datasourceId?: DatasourceId }>> = ({ match }) => {
  const {
    networkId,
    network,
    aqiStandard,
    marker,
    mapSettings,
    pollutantConfigs,
    savedSettings,
    viewport,
    activeLocation,
    isLocationPermissionGranted,
    openSiteRadius,
    availableOpenSites,
    selectedOpenSite,
    setMapSettings,
    setMonitoringDeviceInfos,
    loadSettings,
    setAvailableMarkers,
    setAvailableAqiStandards,
    setDatasourceId,
    setNetwork,
    setOpenSiteActive,
    setDatasourceActive,
    setReverseGeocodedLocationActive,
    setAvailableOpenSites,
  } = useSettings();
  const urlSettings = React.useMemo(() => {
    const { networkId, datasourceId } = match.params;
    return new MapPageUrlSettings(networkId, datasourceId ?? null, window.location.search);
  }, [match]);

  const defaultSettings = React.useMemo(() => {
    return mapSettings ? new DefaultSettings(mapSettings) : null;
  }, [mapSettings]);

  const { aqMap: map } = useMap();
  const isWidget = React.useMemo(() => isWidgetMode(), []);

  const { data: nData, isError: nIsError } = useGetSingleNetwork({ networkId: urlSettings.networkId });
  const { data: mData, isError: mIsError } = useGetAirQualityMarkers({
    network: urlSettings.networkId,
    aqiStandard: aqiStandard?.aqiStandardId,
  });
  const { data: oData, isError: oIsError } = useGetOpenSites({ networkId: urlSettings.networkId });

  const [isLocationOOB, setIsLocationOOB] = React.useState<boolean>(false);
  const [isErrorFetchingLocation, setIsErrorFetchingLocation] = React.useState<boolean>(false);
  const [forceTour, setForceTour] = React.useState<boolean>(false);

  const { isMobile } = useWindowSize();

  const searchedLocation = React.useMemo(() => {
    return activeLocation?.type === ActiveLocationType.ReverseGeocodedLocation ? activeLocation.point : null;
  }, [activeLocation]);

  const [mapSize, setMapSize] = React.useState<{ width: number; height: number } | null>(null);

  /*
   * When the URL provides both an open site id and a zoom level, we have to change the workflow
   * to stop the auto-zoom for the open site.
   */
  const skipInitialAutoZoomRef = React.useRef(urlSettings.openSiteId !== null && urlSettings.zoom !== null);
  React.useEffect(() => {
    if (
      activeLocation &&
      (activeLocation.type !== ActiveLocationType.OpenSite || activeLocation.openSiteId !== urlSettings.openSiteId)
    ) {
      // When the active location changes, we want to re-enable the auto-zoom.
      skipInitialAutoZoomRef.current = false;
    }
  }, [activeLocation, urlSettings.openSiteId]);

  // Precedence order: first is the highest
  const allSettings = React.useMemo(
    () =>
      isWidget
        ? // Don't use saved settings in widget mode
          defaultSettings
          ? [urlSettings, defaultSettings]
          : null
        : defaultSettings && savedSettings
          ? [urlSettings, savedSettings, defaultSettings]
          : null,
    [isWidget, defaultSettings, savedSettings, urlSettings]
  );

  // Load settings once
  React.useEffect(() => {
    if (!allSettings) {
      return;
    }
    loadSettings(allSettings);
  }, [loadSettings, allSettings]);

  // Set map settings and markers when available
  React.useEffect(() => {
    if (!mData) {
      return;
    }
    const { mapSettings, markers, monitoringDeviceInfos } = mData.data;
    setMapSettings(mapSettings);
    setAvailableMarkers(markers);
    setMonitoringDeviceInfos(monitoringDeviceInfos);
  }, [mData, setMapSettings, setAvailableMarkers, setMonitoringDeviceInfos]);

  React.useEffect(() => {
    if (!nData) {
      return;
    }
    const { network, aqiStandards } = nData.data;
    setNetwork(network);
    setAvailableAqiStandards(aqiStandards);
  }, [nData]);

  React.useEffect(() => {
    if (!oData?.data.sites) {
      return;
    }
    setAvailableOpenSites(oData.data.sites);
  }, [oData?.data.sites, setAvailableOpenSites]);

  const mapData: MapData[] | null = React.useMemo(() => {
    return mData?.data.markers.map((marker, index) => new MapData(index, marker)) ?? null;
  }, [mData?.data.markers]);

  // Send an error to sentry if a lot of the markers are stale
  React.useEffect(() => {
    if (!mapData) {
      return;
    }
    const totalMarkers = mapData.length;
    const staleMarkers = mapData.filter(({ marker }) => marker.isStale);
    const staleMarkerCount = staleMarkers.length;
    const staleMarkerPercentage = Number((staleMarkerCount / totalMarkers).toFixed(1));
    if (staleMarkerPercentage > 0.2) {
      Sentry.captureMessage(`A lot of markers are stale: ${staleMarkerPercentage * 100}%`, {
        level: 'error',
        extra: {
          totalMarkers,
          staleMarkerCount,
          staleMarkerPercentage,
          staleReferenceSiteCount: staleMarkers.filter((m) => m.sourceType === SourceType.REFERENCE_SITE).length,
          staleNodeCount: staleMarkers.filter((m) => m.sourceType === SourceType.CLARITY_NODE).length,
          networkId: urlSettings.networkId,
        },
      });
    }
  }, [mapData, urlSettings]);

  const handleGeolocate = React.useCallback(
    (location: Point) => {
      if (!mapData) {
        return;
      }
      const [datasourceId] = findNearestDatasourceId(location.coordinates, mapData);
      setDatasourceId(datasourceId);

      map?.flyTo({
        center: location.coordinates,
        zoom: 10.5,
        speed: 6,
        curve: 1,
        easing: (t: number) => t,
      });
    },
    [map, mapData, setDatasourceId]
  );

  const handleExitDatasourceDetails = React.useCallback(() => {
    trackEvent(AnalyticsEventName.ClosedDatasourceDetails, {
      datasourceId: marker?.datasourceId,
    });
    setDatasourceId(null);
  }, [setDatasourceId, marker]);

  const handleSelectSearchResult = React.useCallback(
    (result: LocationSearchResult) => {
      const { type, location } = result;

      if (type === SearchResultType.Datasource) {
        const markerMapData = result.mapData;
        setDatasourceActive(markerMapData!.marker.datasourceId);
      } else if (type === SearchResultType.OpenSite) {
        setOpenSiteActive(result.siteId);
      } else if (type === SearchResultType.Place) {
        setReverseGeocodedLocationActive(location);
      }
    },
    [setDatasourceActive, setOpenSiteActive, setReverseGeocodedLocationActive]
  );

  // handle active location change
  React.useEffect(() => {
    if (!activeLocation || !map) {
      return;
    }

    const { coordinates } = activeLocation.point;
    let zoom: number | undefined = undefined;
    if (
      activeLocation.type === ActiveLocationType.OpenSite &&
      mapData &&
      openSiteRadius &&
      pollutantConfigs &&
      aqiStandard
    ) {
      const datasourceId = findWorstPollutedDatasourceId(
        coordinates,
        mapData,
        openSiteRadius,
        pollutantConfigs,
        aqiStandard
      );
      if (datasourceId) {
        setDatasourceId(datasourceId);
      }

      const shouldSkipAutoZoom =
        skipInitialAutoZoomRef.current &&
        urlSettings.openSiteId !== null &&
        activeLocation.openSiteId === urlSettings.openSiteId;

      if (!shouldSkipAutoZoom && mapSize) {
        zoom = getZoomForRadius(openSiteRadius, mapSize.width, mapSize.height, coordinates[1]);
      }
    } else if (activeLocation.type === ActiveLocationType.ReverseGeocodedLocation && mapData) {
      const [datasourceId] = findNearestDatasourceId(coordinates, mapData);
      setDatasourceId(datasourceId);
    }

    map.flyTo({
      center: coordinates,
      speed: 2,
      curve: 1,
      zoom,
      easing: (t: number) => t,
    });
  }, [
    map,
    mapSize,
    activeLocation,
    setDatasourceId,
    pollutantConfigs,
    aqiStandard,
    openSiteRadius,
    mapData,
    urlSettings.openSiteId,
  ]);

  const handleLocationError = React.useCallback((error: WLOMException) => {
    if (error instanceof LocationOOBException) {
      setIsLocationOOB(true);
    } else if (error instanceof LocationFetchError) {
      setIsErrorFetchingLocation(true);
    }
  }, []);

  const mapCardComponent = React.useMemo(() => {
    return (
      <MapCard
        onGeoLocate={handleGeolocate}
        onSelectSearchResult={handleSelectSearchResult}
        onError={handleLocationError}
        mapData={mapData ?? []}
        openSites={availableOpenSites ?? []}
      />
    );
  }, [mapData, handleGeolocate, handleSelectSearchResult, handleLocationError, availableOpenSites]);

  const mapMakerLegendComponent = React.useMemo(() => {
    const referenceSiteCount =
      mData?.data.markers.filter((marker) => marker.sourceType === SourceType.REFERENCE_SITE).length ?? 0;
    return (
      aqiStandard && (
        <MapMarkerLegend color={aqiStandard.aqiLevels[0].color} showReferenceSite={referenceSiteCount > 0} />
      )
    );
  }, [aqiStandard, mData]);

  // Change address bar URL when the selected datasource changes
  React.useEffect(() => {
    // Don't update the URL if data is still loading
    // or if the page is in widget mode (otherwise it will
    // stop being recognized as a widget).
    if (!mData || isWidget || !networkId) {
      return;
    }

    // Throttle the URL updates to once every 500ms using a timeout
    const timeoutId = setTimeout(() => {
      updateMapUrl(networkId, viewport, aqiStandard, searchedLocation, marker, selectedOpenSite?.openSiteId);
    }, 500);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
    };
  }, [networkId, mData, marker, viewport, searchedLocation, isWidget, aqiStandard, selectedOpenSite]);

  const mapCardRef = React.useRef<HTMLDivElement>(null);
  const { height: mapCardHeight } = useElementHeight(mapCardRef);

  const datasourceDetailsComponent = React.useMemo(() => {
    const detailsContainer = <DatasourceDetailsContainer onClose={handleExitDatasourceDetails} />;
    if (isMobile) {
      return <BottomSheet firstBreakpointPixels={270}>{detailsContainer}</BottomSheet>;
    }
    if (marker) {
      return <SidePanel topOffsetPixels={mapCardHeight ?? 0}>{detailsContainer}</SidePanel>;
    }
    return null;
  }, [handleExitDatasourceDetails, isMobile, marker, mapCardHeight]);

  if (nIsError || mIsError || oIsError) {
    return <Redirect to="/errors/network-not-found" />;
  }

  const mapPageCss = {
    mapCardContainer: [
      css({
        position: 'fixed',
        zIndex: 3,
        width: 'auto',
        [commonCss.MQSmallerThan.sm]: {
          width: '100%',
        },
      }),
    ],
    mapMakerLegend: [
      css({
        position: 'fixed',
        zIndex: 1,
        bottom: 0,
        left: 10,
        width: 'auto',
      }),
    ],
  };

  return (
    <TourProvider
      steps={UITourSteps}
      showDots={false}
      beforeClose={() => {
        setForceTour(false);
        dismissTour();
      }}
      styles={{
        popover: (base) => ({ ...base, minWidth: '400px' }),
      }}
    >
      <IonPage>
        <IonContent fullscreen>
          {datasourceDetailsComponent}
          {mData && nData && <UITour forceTour={forceTour} disableTour={!!urlSettings.disableTour} />}
          <div ref={mapCardRef} css={mapPageCss.mapCardContainer}>
            {mapCardComponent}
          </div>
          <div css={mapPageCss.mapMakerLegend}>
            {mapMakerLegendComponent}
            <MapAttribution />
          </div>
          <MaplibreMap
            data={mapData ?? undefined}
            onContainerResize={(w, h) => {
              setMapSize({ width: w, height: h });
            }}
          />
          <ToastMsg
            isOpen={isLocationOOB}
            onClose={() => setIsLocationOOB(false)}
            message="Location beyond network bounds"
            type="warning"
          />
          <ToastMsg
            isOpen={isLocationPermissionGranted === false && !activeLocation}
            message="Geolocation permission is denied. Please enable it in your browser settings and reload the app."
            type="danger"
          />
          <ToastMsg
            isOpen={isErrorFetchingLocation}
            message="Error fetching location. Please, try again."
            onClose={() => setIsErrorFetchingLocation(false)}
            type="danger"
          />
          {import.meta.env.DEV ? <BuildInfo /> : null}
          {network && <MapMenuButton network={network} onClickTour={() => setForceTour(true)} />}
          <ExploreFurther delayMs={6_000} />
        </IonContent>
      </IonPage>
    </TourProvider>
  );
};

export default MapPage;
